import React, { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { classInfoService } from '../services/ClassInfoService';
import './StudentProfileView.css';

function StudentProfileView({ student, schoolId }) {
  const [classInfo, setClassInfo] = useState(null);
  const [school, setSchool] = useState(null);
  const [engagementStats, setEngagementStats] = useState({
    assignmentsViewed: 0,
    assignmentsStarted: 0,
    assignmentsSubmitted: 0,
    materialsOpened: 0,
    onlineClassesJoined: 0,
  });
  const [loading, setLoading] = useState(true);

  const loadProfileData = useCallback(async () => {
    try {
      setLoading(true);

      // Load class info
      if (student.classId) {
        const classData = await classInfoService.getClassInfo(student.classId);
        setClassInfo(classData);

        // Load school info using schoolId from props or classData
        const effectiveSchoolId = schoolId || classData.schoolId;
        if (effectiveSchoolId) {
          const schoolDoc = await getDoc(doc(db, 'schools', effectiveSchoolId));
          if (schoolDoc.exists()) {
            setSchool(schoolDoc.data());
          }
        }

        // Load engagement stats
        const activitiesRef = collection(db, 'learningActivities');
        const q = query(activitiesRef, where('studentId', '==', student.studentId));
        const snapshot = await getDocs(q);
        const activities = snapshot.docs.map(doc => doc.data());

        const stats = {
          assignmentsViewed: activities.filter(a => a.activityType === 'Assignment Viewed').length,
          assignmentsStarted: activities.filter(a => a.activityType === 'Assignment Started').length,
          assignmentsSubmitted: activities.filter(a => a.activityType === 'Assignment Submitted').length,
          materialsOpened: activities.filter(a => a.activityType === 'Learning Material Opened').length,
          onlineClassesJoined: activities.filter(a => a.activityType === 'Online Class Joined').length,
        };
        setEngagementStats(stats);
      }
    } catch (error) {
      console.error('Error loading profile data:', error);
    } finally {
      setLoading(false);
    }
  }, [student.classId, student.studentId, schoolId]);

  useEffect(() => {
    loadProfileData();
  }, [loadProfileData]);

  return (
    <div className="student-profile-view">
      <h2>Profile</h2>

      {loading ? (
        <div className="loading">Loading profile...</div>
      ) : (
        <>
          <div className="profile-card">
            <div className="profile-header">
              <div className="profile-avatar">
                {student.firstName ? student.firstName[0].toUpperCase() : 'S'}
              </div>
              <div className="profile-info">
                <h3>{student.fullName || 'Student'}</h3>
                {classInfo && <p className="class-name">{classInfo.name}</p>}
                {school && <p className="school-name">{school.name}</p>}
              </div>
            </div>

            <div className="profile-details">
              <div className="detail-row">
                <span className="detail-label">Student ID:</span>
                <span className="detail-value">{student.studentId || 'N/A'}</span>
              </div>
              {student.firstName && (
                <div className="detail-row">
                  <span className="detail-label">First Name:</span>
                  <span className="detail-value">{student.firstName}</span>
                </div>
              )}
              {student.lastName && (
                <div className="detail-row">
                  <span className="detail-label">Last Name:</span>
                  <span className="detail-value">{student.lastName}</span>
                </div>
              )}
              {student.email && (
                <div className="detail-row">
                  <span className="detail-label">Email:</span>
                  <span className="detail-value">{student.email}</span>
                </div>
              )}
              {student.phoneNumber && (
                <div className="detail-row">
                  <span className="detail-label">Phone:</span>
                  <span className="detail-value">{student.phoneNumber}</span>
                </div>
              )}
            </div>
          </div>

          <h3>Engagement Summary</h3>
          <div className="engagement-grid">
            <div className="engagement-card">
              <span className="engagement-icon">👁️</span>
              <span className="engagement-value">{engagementStats.assignmentsViewed}</span>
              <span className="engagement-label">Assignments Viewed</span>
            </div>
            <div className="engagement-card">
              <span className="engagement-icon">✏️</span>
              <span className="engagement-value">{engagementStats.assignmentsStarted}</span>
              <span className="engagement-label">Assignments Started</span>
            </div>
            <div className="engagement-card">
              <span className="engagement-icon">✅</span>
              <span className="engagement-value">{engagementStats.assignmentsSubmitted}</span>
              <span className="engagement-label">Assignments Submitted</span>
            </div>
            <div className="engagement-card">
              <span className="engagement-icon">📚</span>
              <span className="engagement-value">{engagementStats.materialsOpened}</span>
              <span className="engagement-label">Materials Opened</span>
            </div>
            <div className="engagement-card">
              <span className="engagement-icon">📹</span>
              <span className="engagement-value">{engagementStats.onlineClassesJoined}</span>
              <span className="engagement-label">Online Classes Joined</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default StudentProfileView;
